import { cn } from '@/utils/cn';
import { Link, useLocation } from 'react-router-dom';

interface SidebarLinkProps {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
  setSidebarIsOpen?: (value: boolean) => void;
  isExternal?: boolean;
  notificationCount?: number;
  id?: string;
}

export const SidebarLink = ({
  to,
  children,
  onClick,
  setSidebarIsOpen,
  isExternal = false,
  notificationCount,
  id,
}: SidebarLinkProps) => {
  const location = useLocation();
  const isActive = !isExternal && location.pathname === to;

  // Fonction pour gérer le clic et fermer la sidebar si on est sur mobile
  const handleClick = () => {
    if (window.innerWidth < 1280) {
      setSidebarIsOpen?.(false);
    }
    if (onClick) onClick();
  };

  const baseClasses = cn(
    'group relative flex items-center justify-start gap-3 overflow-visible rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors duration-150',
    isActive ? 'bg-gray-50 text-linko-light' : 'text-gray-800 hover:bg-gray-50',
    'cursor-pointer',
  );

  // Contenu commun pour les deux types de liens
  const linkContent = (
    <>
      {/* Barre d'état actif à gauche */}
      {isActive && (
        <span className="absolute inset-y-0 left-0 w-1 rounded-l-full bg-linko-light" />
      )}
      <div className="relative flex w-full items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">{children}</div>
        {/* Badge de notification sobre */}
        {notificationCount && notificationCount > 0 && (
          <span className="ml-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
            {notificationCount > 9 ? '9+' : notificationCount}
          </span>
        )}
        {/* Indicateur de lien externe */}
        {isExternal && (
          <svg
            className="ml-2 size-3 opacity-60"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        )}
      </div>
    </>
  );

  // Rendu conditionnel en fonction du type de lien (interne ou externe)
  if (isExternal) {
    return (
      <a
        id={id}
        href={to}
        onClick={handleClick}
        className={baseClasses}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkContent}
      </a>
    );
  }

  return (
    <Link id={id} to={to} onClick={handleClick} className={baseClasses}>
      {linkContent}
    </Link>
  );
};
